import { useCallback, useEffect, useMemo, useState } from "react";
import { addDays, format } from "date-fns";
import { es } from "date-fns/locale";
import {
  CalendarOff,
  Clock3,
  LoaderCircle,
  Plane,
  ShieldAlert,
  Trash2
} from "lucide-react";

import {
  createProfessionalLeave,
  deleteProfessionalLeave,
  fetchProfessionalHolidays,
  fetchProfessionalLeaves,
  fetchProfessionalLeaveTypes,
  type ProfessionalHoliday,
  type ProfessionalLeave,
  type ProfessionalLeaveType
} from "../lib/professionalLeavesApi";
import "../styles/ausencias.css";

type LeaveTab = "pending" | "approved" | "denied";

type LeaveListItem = {
  key: string;
  leaveId: number | null;
  title: string;
  typeLabel: string;
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  fullDay: boolean;
  createdAt: string | null;
  status: LeaveTab;
  isCenterClosure: boolean;
};

const VACATION_TOTAL_DAYS = 21;

function toDateOnly(value: Date) {
  const next = new Date(value);
  next.setHours(12, 0, 0, 0);
  return next;
}

function parseYmd(value: string) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1, 12, 0, 0, 0);
}

function formatDate(value?: string | null) {
  if (!value) {
    return "--";
  }

  const date = parseYmd(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, "d MMM yyyy", { locale: es });
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, "d MMM yyyy, HH:mm", { locale: es });
}

function normalizeTime(value?: string | null) {
  if (!value) {
    return null;
  }

  const [hours, minutes] = String(value).split(":");
  if (!hours || !minutes) {
    return value;
  }

  return `${hours}:${minutes}`;
}

function todayAtNoon() {
  return toDateOnly(new Date());
}

function minimumRequestDate() {
  return toDateOnly(addDays(todayAtNoon(), 15));
}

function toInputDate(value: Date) {
  return value.toISOString().slice(0, 10);
}

function getLeaveTypeLabel(leave: ProfessionalLeave) {
  if (leave.type_name?.trim()) {
    return leave.type_name.trim();
  }

  if (leave.title?.trim().toUpperCase() === "CIERRE CENTRO") {
    return "Vacaciones";
  }

  return "Sin tipo";
}

function getLeaveStatus(leave: ProfessionalLeave): LeaveTab {
  if (Number(leave.is_leave_approve) === 1) {
    return "approved";
  }
  if (Number(leave.is_leave_denied) === 1) {
    return "denied";
  }
  return "pending";
}

function countVacationDays(items: LeaveListItem[]) {
  let total = 0;
  let summer = 0;

  for (const item of items) {
    if (item.status !== "approved") {
      continue;
    }

    const normalizedType = item.typeLabel.trim().toLowerCase();
    if (!item.isCenterClosure && normalizedType !== "vacaciones") {
      continue;
    }

    let current = parseYmd(item.startDate);
    const end = parseYmd(item.endDate);
    while (current.getTime() <= end.getTime()) {
      total += 1;
      const month = current.getMonth();
      if (month === 6 || month === 7) {
        summer += 1;
      }
      current = addDays(current, 1);
    }
  }

  return { total, summer };
}

function normalizeError(error: unknown, fallback: string) {
  if (typeof error === "object" && error && "response" in error) {
    const responseError = (error as { response?: { data?: { error?: string } } }).response?.data?.error;
    if (responseError) {
      return responseError;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

export default function AusenciasPage() {
  const [leaveTypes, setLeaveTypes] = useState<ProfessionalLeaveType[]>([]);
  const [leaves, setLeaves] = useState<ProfessionalLeave[]>([]);
  const [holidays, setHolidays] = useState<ProfessionalHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<LeaveTab>("pending");
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [typeId, setTypeId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [fullDay, setFullDay] = useState(true);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const minDate = useMemo(() => minimumRequestDate(), []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [nextLeaveTypes, nextLeaves, nextHolidays] = await Promise.all([
        fetchProfessionalLeaveTypes(),
        fetchProfessionalLeaves(),
        fetchProfessionalHolidays()
      ]);

      setLeaveTypes(nextLeaveTypes);
      setLeaves(nextLeaves);
      setHolidays(nextHolidays);
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudieron cargar tus ausencias"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const mergedItems = useMemo(() => {
    const leaveItems: LeaveListItem[] = leaves.map((leave) => ({
      key: `leave-${leave.leave_id}`,
      leaveId: leave.leave_id,
      title: leave.title?.trim() || "Solicitud",
      typeLabel: getLeaveTypeLabel(leave),
      startDate: leave.start_date,
      endDate: leave.end_date,
      startTime: normalizeTime(leave.start_time),
      endTime: normalizeTime(leave.end_time),
      fullDay: Number(leave.not_available_this_day) === 1,
      createdAt: leave.created_at,
      status: getLeaveStatus(leave),
      isCenterClosure: false
    }));

    const centerClosureItems: LeaveListItem[] = holidays
      .filter((holiday) => holiday.holiday_name?.trim().toUpperCase() === "CIERRE CENTRO")
      .map((holiday) => ({
        key: `holiday-${holiday.holiday_id}`,
        leaveId: null,
        title: holiday.holiday_name || "Cierre centro",
        typeLabel: "Vacaciones",
        startDate: holiday.date,
        endDate: holiday.date,
        startTime: null,
        endTime: null,
        fullDay: true,
        createdAt: holiday.created_at,
        status: "approved" as const,
        isCenterClosure: true
      }));

    return [...leaveItems, ...centerClosureItems].sort((left, right) => {
      const leftDate = `${left.startDate} ${left.startTime || "00:00"}`;
      const rightDate = `${right.startDate} ${right.startTime || "00:00"}`;
      return rightDate.localeCompare(leftDate);
    });
  }, [holidays, leaves]);

  const buckets = useMemo(
    () => ({
      pending: mergedItems.filter((item) => item.status === "pending"),
      approved: mergedItems.filter((item) => item.status === "approved"),
      denied: mergedItems.filter((item) => item.status === "denied")
    }),
    [mergedItems]
  );

  const vacationUsage = useMemo(() => countVacationDays(mergedItems), [mergedItems]);

  const validationError = useMemo(() => {
    if (!title.trim()) {
      return "Anade un motivo para la solicitud.";
    }
    if (!typeId) {
      return "Selecciona un tipo de ausencia.";
    }
    if (!startDate || !endDate) {
      return "Selecciona fecha de inicio y fin.";
    }
    if (startDate > endDate) {
      return "La fecha fin no puede ser anterior al inicio.";
    }
    if (startDate < toInputDate(minDate)) {
      return `La solicitud debe pedirse con al menos 15 dias. Minimo: ${formatDate(toInputDate(minDate))}.`;
    }

    if (!fullDay) {
      if (!startTime || !endTime) {
        return "Indica un tramo horario valido.";
      }
      if (startTime >= endTime) {
        return "La hora fin debe ser posterior a la inicial.";
      }
    }

    return null;
  }, [endDate, endTime, fullDay, minDate, startDate, startTime, title, typeId]);

  async function handleSubmit() {
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await createProfessionalLeave({
        title: title.trim(),
        type_id: Number(typeId),
        start_date: startDate,
        end_date: endDate,
        not_available_this_day: fullDay ? 1 : 0,
        ...(fullDay
          ? {}
          : {
              start_time: `${startTime}:00`,
              end_time: `${endTime}:00`
            })
      });

      setTitle("");
      setTypeId("");
      setStartDate("");
      setEndDate("");
      setFullDay(true);
      setStartTime("");
      setEndTime("");
      setActiveTab("pending");
      await loadData();
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo registrar la solicitud"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(item: LeaveListItem) {
    if (!item.leaveId) {
      return;
    }

    setDeletingKey(item.key);
    setError(null);

    try {
      await deleteProfessionalLeave(item.leaveId);
      await loadData();
    } catch (requestError) {
      setError(normalizeError(requestError, "No se pudo retirar la solicitud"));
    } finally {
      setDeletingKey(null);
    }
  }

  const visibleItems = buckets[activeTab];

  return (
    <main className="shell-page">
      <section className="aus-hero shell-hero-card">
        <div className="shell-hero-icon shell-hero-icon-warning">
          <CalendarOff size={22} />
        </div>
        <div className="shell-hero-copy">
          <span className="shell-eyebrow">Organizacion personal</span>
          <h1>Ausencias y vacaciones</h1>
          <p>Solicita tus ausencias con margen, revisa su estado y consulta los cierres del centro desde la misma pantalla.</p>
        </div>
      </section>

      <section className="aus-stats-grid">
        <article className="aus-stat-card shell-status-card">
          <span className="shell-card-label">Vacaciones usadas</span>
          <strong>
            {vacationUsage.total} / {VACATION_TOTAL_DAYS} dias
          </strong>
          <p>Incluye vacaciones aprobadas y cierres de centro que computan para tu balance anual.</p>
        </article>

        <article className="aus-stat-card shell-status-card">
          <span className="shell-card-label">Julio y Agosto</span>
          <strong>{vacationUsage.summer} dias</strong>
          <p>Control rapido del consumo en los meses de mayor demanda.</p>
        </article>

        <article className="aus-stat-card shell-status-card">
          <span className="shell-card-label">Solicitudes abiertas</span>
          <strong>{buckets.pending.length}</strong>
          <p>Solicitudes todavia pendientes de revision por parte del negocio.</p>
        </article>

        <article className="aus-stat-card shell-status-card">
          <span className="shell-card-label">Cierres del centro</span>
          <strong>
            {mergedItems.filter((item) => item.isCenterClosure).length}
          </strong>
          <p>Fechas ya cerradas por el centro y reflejadas aqui para evitar conflictos.</p>
        </article>
      </section>

      <section className="aus-layout">
        <article className="aus-panel">
          <div className="aus-panel-header">
            <div>
              <span className="aus-panel-kicker">Nueva solicitud</span>
              <h2>Registrar ausencia</h2>
            </div>
            <div className="aus-rule-pill">
              <ShieldAlert size={16} />
              Minimo 15 dias
            </div>
          </div>

          <div className="aus-form-grid">
            <label className="aus-field">
              <span>Motivo</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Ej. Vacaciones de verano"
              />
            </label>

            <label className="aus-field">
              <span>Tipo</span>
              <select value={typeId} onChange={(event) => setTypeId(event.target.value)}>
                <option value="">Selecciona tipo</option>
                {leaveTypes.map((type) => (
                  <option key={type.id} value={String(type.id)}>
                    {type.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="aus-field">
              <span>Inicio</span>
              <input
                type="date"
                min={toInputDate(minDate)}
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </label>

            <label className="aus-field">
              <span>Fin</span>
              <input
                type="date"
                min={startDate || toInputDate(minDate)}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </label>
          </div>

          <div className="aus-form-row">
            <button
              type="button"
              className={`aus-toggle ${fullDay ? "active" : ""}`}
              onClick={() => setFullDay(true)}
            >
              Dia completo
            </button>
            <button
              type="button"
              className={`aus-toggle ${!fullDay ? "active" : ""}`}
              onClick={() => setFullDay(false)}
            >
              Tramo horario
            </button>
          </div>

          {!fullDay ? (
            <div className="aus-form-grid">
              <label className="aus-field">
                <span>Desde</span>
                <input type="time" value={startTime} onChange={(event) => setStartTime(event.target.value)} />
              </label>

              <label className="aus-field">
                <span>Hasta</span>
                <input type="time" value={endTime} onChange={(event) => setEndTime(event.target.value)} />
              </label>
            </div>
          ) : null}

          <div className="aus-help-row">
            <Clock3 size={16} />
            <span>La fecha de inicio debe pedirse con al menos 15 dias de antelacion.</span>
          </div>

          {error ? <div className="aus-error-box">{error}</div> : null}

          <button
            type="button"
            className="aus-submit-button"
            disabled={Boolean(validationError) || submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting ? (
              <>
                <LoaderCircle size={16} className="spin" />
                Enviando...
              </>
            ) : (
              <>
                <Plane size={16} />
                Enviar solicitud
              </>
            )}
          </button>
        </article>

        <article className="aus-panel">
          <div className="aus-panel-header">
            <div>
              <span className="aus-panel-kicker">Seguimiento</span>
              <h2>Tus solicitudes</h2>
            </div>
            <button type="button" className="aus-refresh-button" onClick={() => void loadData()}>
              Actualizar
            </button>
          </div>

          <div className="aus-tabs">
            {([
              { key: "pending", label: "Pendientes", count: buckets.pending.length },
              { key: "approved", label: "Aprobadas", count: buckets.approved.length },
              { key: "denied", label: "Denegadas", count: buckets.denied.length }
            ] as const).map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`aus-tab ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                <span>{tab.count}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="aus-loading-list">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="aus-skeleton" />
              ))}
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="aus-empty-state">No hay elementos en esta pestaña.</div>
          ) : (
            <div className="aus-list">
              {visibleItems.map((item) => (
                <article key={item.key} className={`aus-leave-card ${item.status}`}>
                  <div className="aus-leave-top">
                    <div>
                      <div className="aus-leave-title-row">
                        <strong>{item.title}</strong>
                        {item.isCenterClosure ? <span className="aus-chip muted">Cierre centro</span> : null}
                      </div>
                      <div className="aus-leave-meta">
                        <span>{item.typeLabel}</span>
                        <span>
                          {formatDate(item.startDate)} - {formatDate(item.endDate)}
                        </span>
                        <span>
                          {item.fullDay ? "Dia completo" : `${item.startTime} - ${item.endTime}`}
                        </span>
                      </div>
                    </div>

                    <span className="aus-chip status">
                      {item.status === "pending"
                        ? "Pendiente"
                        : item.status === "approved"
                          ? "Aprobada"
                          : "Denegada"}
                    </span>
                  </div>

                  <div className="aus-leave-bottom">
                    <span>Creada {formatDateTime(item.createdAt)}</span>
                    {item.status === "pending" && item.leaveId ? (
                      <button
                        type="button"
                        className="aus-delete-button"
                        disabled={deletingKey === item.key}
                        onClick={() => void handleDelete(item)}
                      >
                        <Trash2 size={14} />
                        {deletingKey === item.key ? "Retirando..." : "Retirar"}
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          )}
        </article>
      </section>
    </main>
  );
}
